import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Edit3 } from "lucide-react";
import { adminGins, adminUpdateGin } from "../../lib/adminApi";

const AVAILABILITY_TONE = {
  AVAILABLE: "border-green-700/40 text-green-400",
  NOT_AVAILABLE: "border-amber-600/40 text-amber-400",
  HIDDEN: "border-white/15 text-white/40",
};

export default function AdminMenu() {
  const [gins, setGins] = useState([]);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("All");
  const [avail, setAvail] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => { adminGins().then((d) => { setGins(d.gins); setLoading(false); }); }, []);

  const countries = useMemo(() => ["All", ...Array.from(new Set(gins.map((g) => g.country))).sort()], [gins]);
  const filtered = useMemo(() =>
    gins.filter((g) =>
      (country === "All" || g.country === country) &&
      (avail === "All" || g.availability_status === avail) &&
      (q.trim() === "" || g.name.toLowerCase().includes(q.toLowerCase()) || g.id.includes(q.toLowerCase()))
    ), [gins, q, country, avail]);

  const setAvailability = async (id, status) => {
    await adminUpdateGin(id, { availability_status: status });
    setGins((arr) => arr.map((g) => g.id === id ? { ...g, availability_status: status, is_available: status === "AVAILABLE" } : g));
  };

  return (
    <div className="px-5 md:px-10 py-8 max-w-7xl" data-testid="admin-menu">
      <header className="mb-7">
        <p className="overline text-[#C17F59] mb-2">Menu Manager</p>
        <h1 className="font-display text-4xl">{gins.length} gins on the shelf.</h1>
        <p className="text-white/60 text-sm mt-2 max-w-2xl">Edit price, ABV, copy, mood, taste, DNA, image — and toggle availability. Public site updates instantly.</p>
      </header>

      {/* Availability filter chips */}
      <div className="flex flex-wrap gap-1 mb-3 border-b border-white/8 overflow-x-auto no-scrollbar" data-testid="availability-chips">
        {[
          { k: "All", l: `All (${gins.length})` },
          { k: "AVAILABLE", l: `Available (${gins.filter((g) => g.availability_status === "AVAILABLE").length})` },
          { k: "NOT_AVAILABLE", l: `Not Available (${gins.filter((g) => g.availability_status === "NOT_AVAILABLE").length})` },
          { k: "HIDDEN", l: `Hidden (${gins.filter((g) => g.availability_status === "HIDDEN").length})` },
        ].map((c) => (
          <button key={c.k} onClick={() => setAvail(c.k)}
                  className={`text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors ${avail === c.k ? "text-[#C17F59] border-b border-[#C17F59]" : "text-white/50 hover:text-white"}`}
                  data-testid={`avail-chip-${c.k}`}>
            {c.l}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2 border-b border-white/15 px-2 py-2 flex-1 min-w-[260px]">
          <Search size={14} className="text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or id..."
                 className="bg-transparent text-white w-full text-sm focus:outline-none placeholder:text-white/30"
                 data-testid="admin-menu-search" />
        </div>
        <select value={country} onChange={(e) => setCountry(e.target.value)}
                className="bg-[#15171A] text-white border border-white/10 px-3 py-2 text-sm focus:outline-none"
                data-testid="admin-menu-country">
          {countries.map((c) => <option key={c} className="bg-[#15171A] text-white">{c}</option>)}
        </select>
      </div>

      {loading && <p className="text-white/40">Loading...</p>}

      <div className="bg-[#15171A] border border-white/8 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-menu-table">
          <thead>
            <tr className="border-b border-white/8 text-left">
              <th className="py-3 px-4 overline text-white/40">Gin</th>
              <th className="py-3 px-4 overline text-white/40">Country</th>
              <th className="py-3 px-4 overline text-white/40 text-right">₹/30ml</th>
              <th className="py-3 px-4 overline text-white/40 text-right">ABV</th>
              <th className="py-3 px-4 overline text-white/40">Availability</th>
              <th className="py-3 px-4 overline text-white/40">Image</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((g) => (
              <tr key={g.id} className={`border-b border-white/4 hover:bg-white/3 transition-colors ${g.availability_status === "HIDDEN" ? "opacity-50" : ""}`} data-testid={`admin-menu-row-${g.id}`}>
                <td className="py-3 px-4">
                  <p className="font-display text-base">{g.name}</p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{g.styles?.[0]}</p>
                </td>
                <td className="py-3 px-4 text-white/70">{g.country}</td>
                <td className="py-3 px-4 text-right tabular-nums">₹{g.price}</td>
                <td className="py-3 px-4 text-right tabular-nums">{g.abv}%</td>
                <td className="py-3 px-4">
                  <select value={g.availability_status || "AVAILABLE"}
                          onChange={(e) => setAvailability(g.id, e.target.value)}
                          className={`bg-[#101113] border px-2 py-1 text-[10px] uppercase tracking-[0.18em] focus:outline-none ${AVAILABILITY_TONE[g.availability_status || "AVAILABLE"]}`}
                          data-testid={`avail-select-${g.id}`}>
                    <option value="AVAILABLE" className="bg-[#15171A] text-white">Available</option>
                    <option value="NOT_AVAILABLE" className="bg-[#15171A] text-white">Not Available</option>
                    <option value="HIDDEN" className="bg-[#15171A] text-white">Hidden</option>
                  </select>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] uppercase tracking-[0.18em] ${g.image_state === "approved" ? "text-green-400" : "text-white/40"}`}>
                    {g.image_state || "placeholder"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <Link to={`/admin/menu/${g.id}`}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-[#C17F59] hover:text-[#D89A74]"
                        data-testid={`admin-edit-${g.id}`}>
                    <Edit3 size={12} /> Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
